import cornerstoneTools from 'cornerstone-tools';
import computeAllRoiSuv from './computeAllRoiSuv';
import computeRoiSuvForPairs from './computeRoiSuvForPairs';
import { discoverCTPETPairs } from './suvDiscovery';
import { clearAllSuvSamples, SUV_METHOD_VERSION } from './suvSampleCache';
import computeRoiPsmaVolumes from './computeRoiPsmaVolumes';
import { PSMA_METHOD_VERSION } from './psmaVolume';
import { findRefBloodPoolROI, computeBloodPoolRef } from './newLesionUptake';
import {
  clearAllSuvStats,
  setSuvComputingFlag,
  setPsmaEntries,
  setBloodPoolRefs,
  makeSuvKey,
  getSuvStats,
  setSuvStats,
  makeNeStats,
} from './suvStore';

const EDIT_DEBOUNCE_MS = 300;

/**
 * Per-viewer state, from initSuv to teardownSuv. Every field is defined here,
 * so the shape never changes. An async step keeps its own session and checks
 * `s === session` before touching redux: teardown only swaps the object.
 */
function createSession({
  idle = false,
  imageSets = [],
  enabled = false,
  getStructureSet = () => null,
} = {}) {
  return {
    idle, // true only for NO_SESSION: initSuv has not run (or was torn down)
    imageSets,
    enabled: !!enabled, // PET-CT fusion with an RT, feature flag on
    getStructureSet,
    inFlight: 0, // full pass + scoped recomputes
    editTimers: new Map(), // `${structureSetUID}:${ROINumber}` -> timeout handle
    storedPsmaByKey: new Map(), // key -> stored psma_volume (display-only)
  };
}

/**
 * Before initSuv and after teardownSuv. Never enabled, and blocks saves.
 * Frozen: a write to it (e.g. begin() on it) throws instead of passing silently.
 */
const NO_SESSION = Object.freeze(createSession({ idle: true }));

let session = NO_SESSION;

/** Not per session: RTEdit and ExtendedRTPanel set these in either order. */
let psmaEnabled = false;
let newUptakeCheckEnabled = false;

function begin(s) {
  s.inFlight += 1;
  if (s === session && s.inFlight === 1) setSuvComputingFlag(true);
}

function end(s) {
  s.inFlight = Math.max(0, s.inFlight - 1);
  if (s === session && s.inFlight === 0) setSuvComputingFlag(false);
}

function editModule() {
  return cornerstoneTools.getModule('rtstruct-edit');
}

/* ---- stored values (from the saved RT) ----------------------------------
 * Display-only. Never written back: a save waits for live values.
 * - SUV: ROIs whose saved suv_method matches SUV_METHOD_VERSION are seeded
 *   into redux with source: 'stored'; a live result overwrites the entry.
 * - PSMA-VOL: shown while no edit has happened (any ROI, any structure set)
 *   and the ROI still shows stored SUV; the first edit drops all of them.
 */

const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);

function seedStoredSuv() {
  const { storedPsmaByKey } = session;
  for (const { structureSetUID, ctSeriesUID } of discoverCTPETPairs(session.imageSets)) {
    const rois = session.getStructureSet(structureSetUID)?.ROIContours || [];
    for (const roi of rois) {
      const { stored } = roi;
      if (!stored?.suv || stored.suv_method !== SUV_METHOD_VERSION) continue;
      const { max, mean, min, sd } = stored.suv;
      if (![max, mean, min].every(isFiniteNumber)) continue;

      const key = makeSuvKey(structureSetUID, roi.ROINumber, ctSeriesUID);
      setSuvStats(key, {
        suvMax: max,
        suvMean: mean,
        suvMin: min,
        suvSD: sd,
        voxelCount: null, // not saved
        ne: false,
        reason: null,
        source: 'stored',
      });
      if (stored.psma_volume && stored.psma_method === PSMA_METHOD_VERSION) {
        storedPsmaByKey.set(key, stored.psma_volume);
      }
    }
  }
}

/** Stored PSMA-VOL of a key, only while its SUV is still the stored one. */
function getStoredPsma(key) {
  const stored = session.storedPsmaByKey.get(key);
  if (!stored || getSuvStats(key)?.source !== 'stored') return null;
  return { ...stored, source: 'stored' };
}

/** After the full pass: no stored value may remain. */
function finalizeStored() {
  session.storedPsmaByKey.clear();
  for (const { structureSetUID, ctSeriesUID } of discoverCTPETPairs(session.imageSets)) {
    const rois = session.getStructureSet(structureSetUID)?.ROIContours || [];
    for (const roi of rois) {
      const key = makeSuvKey(structureSetUID, roi.ROINumber, ctSeriesUID);
      if (getSuvStats(key)?.source === 'stored') {
        console.warn('[SUV] stored value not replaced by the live pass', key);
        setSuvStats(key, makeNeStats('stored-not-recomputed'));
      }
    }
  }
}

/** An edited ROI shows '…' (stored or live value) until its scoped recompute. */
function markRoiPending(structureSetUID, ROINumber) {
  const pair = discoverCTPETPairs(session.imageSets).find(
    (p) => p.structureSetUID === structureSetUID
  );
  if (!pair) return;
  setSuvStats(makeSuvKey(structureSetUID, ROINumber, pair.ctSeriesUID), null);
}

/* ---- PSMA-VOL ----------------------------------------------------------- */

/** Called by RTEdit once the scenario guideline is known. */
export function setPsmaEnabled(enabled) {
  psmaEnabled = !!enabled;
  refreshPsma();
}

/**
 * Recompute PSMA-VOL for every CT-PET pair from the cached samples and write
 * it to redux in one dispatch. Counting only; no re-sampling.
 */
export function refreshPsma() {
  if (!session.enabled || !psmaEnabled) {
    setPsmaEntries({}, {});
    return;
  }
  const links = editModule().getters.links() || [];
  const psmaByKey = {};
  const psmaThresholdsBySS = {};
  for (const { structureSetUID, ctSeriesUID } of discoverCTPETPairs(session.imageSets)) {
    const rois = session.getStructureSet(structureSetUID)?.ROIContours || [];
    const { thresholds, byROINumber } = computeRoiPsmaVolumes({
      structureSetUID,
      ctSeriesUID,
      rois,
      links,
    });
    psmaThresholdsBySS[structureSetUID] = thresholds;
    for (const [ROINumber, entry] of byROINumber) {
      const key = makeSuvKey(structureSetUID, ROINumber, ctSeriesUID);
      psmaByKey[key] = entry.psma_volume ?? getStoredPsma(key);
    }
  }
  setPsmaEntries(psmaByKey, psmaThresholdsBySS);
}

/* ---- new-lesion uptake check (display-only, optional) -------------------
 * Lesion SUVmax > REF-BLOODPOOL SUVmax, a RECIP 1.0 reminder for the reader.
 * Separate from PSMA-VOL: own flag, own redux entry, never saved, never
 * blocks a save. Only the blood-pool reference is stored; the per-lesion
 * comparison is derived where it is shown (compareNewLesionUptake).
 */

/** Called by RTEdit once the scenario guideline is known. */
export function setNewUptakeCheckEnabled(enabled) {
  newUptakeCheckEnabled = !!enabled;
  refreshBloodPool();
}

function refreshBloodPool() {
  if (!session.enabled || !newUptakeCheckEnabled) {
    setBloodPoolRefs({});
    return;
  }
  const links = editModule().getters.links() || [];
  const bloodPoolBySS = {};
  for (const { structureSetUID, ctSeriesUID } of discoverCTPETPairs(session.imageSets)) {
    const rois = session.getStructureSet(structureSetUID)?.ROIContours || [];
    const { roi, reason } = findRefBloodPoolROI(rois, links);
    const stats = roi
      ? getSuvStats(makeSuvKey(structureSetUID, roi.ROINumber, ctSeriesUID))
      : null;
    bloodPoolBySS[structureSetUID] = computeBloodPoolRef(stats, reason);
  }
  setBloodPoolRefs(bloodPoolBySS);
}

/**
 * Everything derived from SUV stats + links. The paths are independent:
 * a failure in one must not stop the other.
 * Triggers: initSuv, an edit, a scoped recompute, a links change (RTEdit).
 */
export function refreshDerived() {
  const paths = [
    ['PSMA-VOL', refreshPsma],
    ['blood pool', refreshBloodPool],
  ];
  for (const [label, refresh] of paths) {
    try {
      refresh();
    } catch (err) {
      console.warn(`[SUV] ${label} refresh failed`, err);
    }
  }
}

/* ---- save --------------------------------------------------------------- */

/**
 * Why a save must wait, or null when it may proceed.
 * Saves use live values only: the full pass and all edit recomputes must finish.
 * Not initialised yet (NO_SESSION) blocks; only "no PET-CT fusion" saves NE.
 */
export function getSuvSaveBlocker() {
  if (session.idle) return 'SUV is still loading.';
  if (!session.enabled) return null; // no PET-CT fusion: the writer saves NE
  if (session.inFlight > 0 || session.editTimers.size > 0) {
    return 'SUV / PSMA-VOL is still being computed.';
  }
  return null;
}

/* ---- edits -------------------------------------------------------------- */

/**
 * Debounced per ROI so a multi-slice edit collapses into one re-sample.
 * The timer is deleted and begin() runs in the same tick, so the save blocker
 * never sees a gap between the two.
 */
export function onPETCTContourCommitted(structureSetUID, ROINumber) {
  if (!session.enabled) return;
  if (!structureSetUID || ROINumber == null) return;

  const s = session;
  const timerKey = `${structureSetUID}:${ROINumber}`;
  const prev = s.editTimers.get(timerKey);
  if (prev) clearTimeout(prev);

  s.editTimers.set(
    timerKey,
    setTimeout(() => {
      s.editTimers.delete(timerKey);
      begin(s);
      computeRoiSuvForPairs({
        imageSets: s.imageSets,
        getStructureSet: s.getStructureSet,
        structureSetUID,
        roiNumbers: [ROINumber],
      })
        .then(() => {
          if (s === session) refreshDerived();
        })
        .catch((err) => console.warn('[SUV] scoped recompute failed', err))
        .finally(() => end(s));
    }, EDIT_DEBOUNCE_MS)
  );
}

function handleROIContourChanged(evt) {
  if (!session.enabled) return;
  const { structureSetUID, ROINumbers } = evt.detail;
  // Any edit may move a REF-LIVER threshold: stop showing stored PSMA-VOL.
  session.storedPsmaByKey.clear();
  ROINumbers.forEach((ROINumber) => {
    markRoiPending(structureSetUID, ROINumber);
    onPETCTContourCommitted(structureSetUID, ROINumber);
  });
  refreshDerived(); // once per event: '…' for edited ROIs and dropped stored PSMA
}

/* ---- lifecycle ---------------------------------------------------------- */

export function teardownSuv() {
  editModule().removeEventListener('ROIContourChanged', handleROIContourChanged);
  for (const t of session.editTimers.values()) clearTimeout(t);
  session = NO_SESSION; // async steps of the old session see s !== session
  setSuvComputingFlag(false);
  clearAllSuvSamples();
  clearAllSuvStats();
}

export async function initSuv({ imageSets, enabled, getStructureSet }) {
  teardownSuv();
  const s = createSession({ imageSets, enabled, getStructureSet });
  session = s;
  if (!s.enabled) return;

  editModule().addEventListener('ROIContourChanged', handleROIContourChanged);

  begin(s); // suvComputing = true until the full pass ends; saves wait
  try {
    seedStoredSuv();
    refreshDerived();
    await computeAllRoiSuv({ imageSets, getStructureSet });
  } catch (err) {
    console.warn('[SUV] initSuv failed', err);
  } finally {
    if (s === session) {
      // not torn down meanwhile
      finalizeStored();
      refreshDerived();
    }
    end(s);
  }
}